import { useEffect, useRef, useState } from 'react';
import { clearApolloCache } from '@/shared/api';
import { handleRecaptureAutoEvents } from '@/entities/event';
import { EMITTER_CONSTANTS, KEY_RECOIL, STRIP_MANAGEMENT_THUMBNAIL_INFO } from '@/shared/config';
import { useEmitter, usePaginationKeyboard } from '@/shared/lib/customHooks';
import emitter from '@/shared/lib/eventEmitter';
import { createOnChangePageString } from '@/shared/lib/parsePageInput';
import { toastrError } from '@/shared/lib/toastNotification';
import { selectPageIndex, selectStripFilter, useReportStore } from '@/entities/report';
import { ID_DIV_STRIP_MANAGEMENT } from '../handler';
import type { PaginationContainerProps } from './index';

/**
 * Drives the pagination bar of the Strip Management tab, exposing page state, the capture-type filter and every control handler. Derives the page count from the total strip count and the per-page limit, keeps the typed page number in sync with the stored page index, wires the Z and X shortcuts plus the next and previous emitter events, and triggers auto-event recapture.
 *
 * @param props - The pagination container props, whose isDisabled flag suppresses keyboard navigation.
 * @returns An object carrying the page state, filter state and the pagination handlers.
 */
const usePaginationContainer = ({ isDisabled }: Pick<PaginationContainerProps, 'isDisabled'>) => {
  const keyRecoil = KEY_RECOIL.STRIP_MANAGEMENT;

  // reportInfoState (Recoil, Task 7) is typed {} | IReportInfo (atom default is {}) - pre-existing gap.
  const { studyId, profileId } = useReportStore((s) => s.reportInfo) as any;
  const pageIndex = useReportStore(selectPageIndex(keyRecoil));
  const setPageIndexAction = useReportStore((s) => s.actions.setPageIndex);
  /**
   * Writes the strip tab page index into the report store. Forwards the value to the store action together with the TAB_3 key.
   *
   * @param v - The new page index, or an updater receiving the previous one.
   * @returns Nothing.
   */
  const setPageIndex = (v: Parameters<typeof setPageIndexAction>[1]) =>
    setPageIndexAction(keyRecoil, v);
  const stripFilter = useReportStore(selectStripFilter(keyRecoil));
  const setStripFilterAction = useReportStore((s) => s.actions.setStripFilter);
  /**
   * Writes the strip filter of the strip tab into the report store. Forwards the value to the store action together with the TAB_3 key.
   *
   * @param v - The new strip filter, or an updater receiving the previous one.
   * @returns Nothing.
   */
  const setStripFilter = (v: Parameters<typeof setStripFilterAction>[1]) =>
    setStripFilterAction(keyRecoil, v);
  const setSelectedMultipleStripAction = useReportStore((s) => s.actions.setSelectedMultipleStrip);
  /**
   * Writes the multi-strip selection of the strip tab into the report store. Forwards the value to the store action together with the TAB_3 key.
   *
   * @param v - The new selection array, or an updater receiving the previous one.
   * @returns Nothing.
   */
  const setSelectedMultipleStrip = (v: Parameters<typeof setSelectedMultipleStripAction>[1]) =>
    setSelectedMultipleStripAction(keyRecoil, v);
  const setIsRenderViewerEcg = useReportStore((s) => s.actions.setIsRenderViewerEcg);
  const setWaitingAction = useReportStore((s) => s.actions.setIsWaiting);
  /**
   * Sets the waiting flag of the strip tab in the report store. Forwards the value to the store action together with the TAB_3 key.
   *
   * @param v - The new waiting flag, or an updater receiving the previous one.
   * @returns Nothing.
   */
  const setWaiting = (v: Parameters<typeof setWaitingAction>[1]) => setWaitingAction(keyRecoil, v);
  const totalStrip = useReportStore((s) => s.stripTotalStrip);

  const [inputSize, setInputSize] = useState(String(pageIndex.index + 1).length + 1);
  const [pageString, setPageString] = useState<any>(pageIndex.index + 1);
  const [isOpenModaltrip, setIsOpenModalStrip] = useState(false);
  const [isError, setIsError] = useState(false);
  const [filterValue, setFilterValue] = useState(stripFilter.showCaptureType || 'ALL');
  const [visible, setVisible] = useState(false);

  const pageStringRef = useRef(pageString);
  const maxPageNumberRef = useRef<any>();

  const STEP = STRIP_MANAGEMENT_THUMBNAIL_INFO.stripDisplayLimit;

  const maxPageNumber = totalStrip ? Math.ceil(totalStrip / STEP) : 1;
  maxPageNumberRef.current = maxPageNumber;

  const isDisabledNext = (pageIndex.index + 1) * STEP >= totalStrip;
  const isDisabledPrev = pageIndex.index === 0;

  /**
   * Moves the strip grid back one page. Decrements the stored page index and marks the ECG viewer for re-render.
   *
   * @returns Nothing.
   */
  const onClickPrevious = () => {
    setPageIndex({ index: pageIndex.index - 1 });
    setIsRenderViewerEcg(true);
  };

  /**
   * Advances the strip grid to the next page. Increments the stored page index and marks the ECG viewer for re-render.
   *
   * @returns Nothing.
   */
  const onClickNext = () => {
    setPageIndex({ index: pageIndex.index + 1 });
    setIsRenderViewerEcg(true);
  };

  /**
   * Asks the backend to recapture the auto-detected event strips of the current profile. Builds a request id, persists it to localStorage so the later reply can be matched, calls the recapture mutation, and on failure shows an error toast and clears the waiting flag.
   *
   * @returns A promise resolving once the recapture request has been sent and any failure reported.
   */
  const onClickReCaptureStrip = async () => {
    setWaiting(true);
    try {
      const requestId = `requests:${profileId}:${Date.now()}`;
      const keyStorage = `requestId-recapture-${profileId}`;
      localStorage.setItem(keyStorage, JSON.stringify(requestId));
      const result = await handleRecaptureAutoEvents({
        studyId,
        profileId,
        requestId,
      });
      if (result.isSuccess) {
        // setWaiting(true);
      } else {
        toastrError('Failed to recapture auto events!');
        setWaiting(false);
      }
    } catch (error) {
      console.log('Error recapturing auto events:', error);
      toastrError('Failed to recapture auto events!');
      setWaiting(false);
    }
  };

  /**
   * Opens or closes the capture-type filter dropdown of the strip pagination bar. Stores the incoming flag as the dropdown visibility state.
   *
   * @param flag - Whether the filter dropdown should be shown.
   * @returns Nothing.
   */
  const handleVisibleChange = (flag: any) => {
    setVisible(flag);
  };

  const onChangePageString = createOnChangePageString({
    setIsError,
    setPageString,
    pageStringRef,
    setInputSize,
    setIsRenderViewerEcg,
  });

  /**
   * Commits the manually typed page number when the page input loses focus. Parses the input, moves the stored page index when the number is within the maximum page count, and raises the error flag otherwise.
   *
   * @returns Nothing.
   */
  const onBlur = () => {
    if (pageString) {
      const pageNumber = Number(pageString);
      if (pageNumber && pageNumber <= maxPageNumber) {
        const newPageIndex = Number(pageString) - 1;
        setPageIndex((prev) => (newPageIndex !== prev.index ? { index: newPageIndex } : prev));
      } else {
        setIsError(true);
      }
    }
  };

  /**
   * Toggles hiding strips that are excluded from the report. Clears the multi-strip selection and writes the new isHideExcludeReport flag into the strip filter.
   *
   * @param checked - Whether excluded strips should be hidden.
   * @returns Nothing.
   */
  const onChangeSwitch = (checked: any) => {
    setSelectedMultipleStrip([]);
    setStripFilter({ ...stripFilter, isHideExcludeReport: checked });
  };
  /**
   * Refetches the strip list after dropping cached GraphQL results. Awaits the Apollo cache clear, then emits the RELOAD_EVENT_STRIP event so the strip grid reloads.
   *
   * @returns A promise resolving once the cache is cleared and the reload event has been emitted.
   */
  const onClickReloadButton = async () => {
    await clearApolloCache();
    emitter.emit(EMITTER_CONSTANTS.RELOAD_EVENT_STRIP);
  };

  /**
   * Records the capture-type option a user picked in the strip filter dropdown. Reads the value prop off the clicked menu item and holds it as the pending filter value until the filter is applied.
   *
   * @param item - The clicked dropdown menu item whose value prop carries the capture type.
   * @returns Nothing.
   */
  const onClickItem = ({ item }: any) => {
    setFilterValue(item.props.value);
  };

  /**
   * Applies the pending capture-type choice to the strip filter. Closes the filter dropdown and writes the selected value into showCaptureType.
   *
   * @returns Nothing.
   */
  const onClickApplyFilter = () => {
    setVisible(false);
    setStripFilter({ ...stripFilter, showCaptureType: filterValue });
  };

  const { onKeyDown } = usePaginationKeyboard({
    tabZoneId: ID_DIV_STRIP_MANAGEMENT,
    isDisabled,
    isBlocked: isOpenModaltrip,
    isDisabledPrev,
    isDisabledNext,
    onClickPrevious,
    onClickNext,
    pageStringRef,
    maxPageNumberRef,
    setPageIndex,
    setIsError,
    deps: [pageIndex, totalStrip, isDisabled, isOpenModaltrip],
  });

  useEffect(() => {
    setPageString(pageIndex.index + 1);
    setInputSize(String(pageIndex.index + 1).length + 1);
  }, [pageIndex]);

  useEmitter(
    EMITTER_CONSTANTS.STRIP_MANAGEMENT_NEXT,
    () => {
      if (!isDisabledNext) {
        onClickNext();
      }
    },
    [pageIndex, totalStrip],
  );

  useEmitter(
    EMITTER_CONSTANTS.STRIP_MANAGEMENT_PREV,
    () => {
      if (!isDisabledPrev) {
        onClickPrevious();
      }
    },
    [pageIndex, totalStrip],
  );

  useEmitter(
    EMITTER_CONSTANTS.OPEN_MODAL_EDIT_AI_STRIP,
    (msg: any) => {
      setIsOpenModalStrip(msg.value);
    },
    [],
  );

  useEffect(() => {
    if (maxPageNumber <= pageIndex.index) {
      setPageIndex({ index: maxPageNumber - 1 });
    }
  }, [maxPageNumber, pageIndex]);

  return {
    stripFilter,
    isDisabledPrev,
    isDisabledNext,
    maxPageNumber,
    pageString,
    inputSize,
    isError,
    visible,
    filterValue,
    onClickPrevious,
    onClickNext,
    onClickReCaptureStrip,
    handleVisibleChange,
    onChangePageString,
    onKeyDown,
    onBlur,
    onChangeSwitch,
    onClickReloadButton,
    onClickItem,
    onClickApplyFilter,
  };
};

export default usePaginationContainer;
